/**
 * The "Activity" tab: what was tracked about the user — step records, trails and walks, reviews,
 * and the activity feed — with edit and delete.
 *
 * Each section owns its own query and mutation; they share nothing but the user id. A change in
 * one can still move another (deleting a walk's steps changes the lifetime total), which the
 * shared invalidation in useUserDataChange takes care of.
 */

import { useState } from 'react'
import { Box } from '@mui/material'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { UserStepsSection } from './UserStepsSection'
import { UserTrailsSection } from './UserTrailsSection'
import { UserReviewsSection } from './UserReviewsSection'
import { UserActivityFeedSection } from './UserActivityFeedSection'

export interface UserActivityPanelProps {
  userId: string
}

const SECTIONS: ResponsiveTabItem[] = [
  { key: 'steps', label: 'Steps' },
  { key: 'trails', label: 'Trails & walks' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'feed', label: 'Feed' },
]

/**
 * The sections used to stack, one table under the next: on a phone the feed was several screens
 * of scrolling below the steps. Sub-tabs show one at a time, on their own `?activity=` param so
 * the page's `?tab=activity` stays put, and only the open one fetches.
 */
export function UserActivityPanel({ userId }: UserActivityPanelProps) {
  const [section, setSection] = useState('steps')
  return (
    <Box>
      <ResponsiveTabs tabs={SECTIONS} value={section} onChange={setSection} paramName="activity" sx={{ mb: 2 }} />
      {section === 'steps' && <UserStepsSection userId={userId} />}
      {section === 'trails' && <UserTrailsSection userId={userId} />}
      {section === 'reviews' && <UserReviewsSection userId={userId} />}
      {section === 'feed' && <UserActivityFeedSection userId={userId} />}
    </Box>
  )
}
